import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import * as React from 'react';
import { DEFAULT_HIDE_DELAY, DEFAULT_MAX_TOASTS_NUMBER, DEFAULT_MAX_TOASTS_NUMBER_MOBILE, hidableToasts } from '../../internal/constants/toast';
import { calculateHideDelay, findOldestToastIdToHide, getIsMaxToastsNumberExceeded, getToastPlacement } from '../internal/utils';
import { useDeviceType } from '../../responsive/hooks/useDeviceType';
import { useUiKitToastApiContext, useUiKitToastsContext } from '../../internal/context';
export function useToast() {
  let initOptions = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  const {
    placement,
    size = 'M',
    maxToastsNumber,
    hideDelay = DEFAULT_HIDE_DELAY,
    indent
  } = initOptions;
  const deviceType = useDeviceType();
  const toastsContext = useUiKitToastsContext();
  const toastsApiContext = useUiKitToastApiContext();
  const isMobileDevice = deviceType === 'phone';
  const placementFinal = getToastPlacement(placement, isMobileDevice);
  const maxToastsNumberFinal = maxToastsNumber !== null && maxToastsNumber !== void 0 ? maxToastsNumber : isMobileDevice ? DEFAULT_MAX_TOASTS_NUMBER_MOBILE : DEFAULT_MAX_TOASTS_NUMBER;
  const currentToastsStackApi = toastsContext.toastsStacksApi[placementFinal];
  const setToastsStack = currentToastsStackApi[1];
  const hide = React.useCallback(id => {
    setToastsStack(currentToastsStack => {
      const toastIndexToHide = currentToastsStack.findIndex(toast => toast.id === id);
      if (toastIndexToHide !== -1) {
        currentToastsStack[toastIndexToHide].hidden = true;
        return [...currentToastsStack];
      }
      return currentToastsStack;
    });
  }, [setToastsStack]);
  const hideWithDelay = React.useCallback((id, toastHideDelay) => {
    const delay = calculateHideDelay(toastHideDelay, hideDelay);
    const timerId = window.setTimeout(() => hide(id), delay);
    return timerId;
  }, [hide, hideDelay]);
  const hideAll = React.useCallback(() => {
    setToastsStack(currentToastsStack => {
      currentToastsStack.forEach(toast => hide(toast.id));
      return [...currentToastsStack];
    });
  }, [setToastsStack, hide]);
  const update = React.useCallback((id, updateData) => {
    setToastsStack(currentToastsStack => {
      const toastIndexToUpdate = currentToastsStack.findIndex(toast => toast.id === id);
      if (toastIndexToUpdate !== -1) {
        const currentToast = currentToastsStack[toastIndexToUpdate];
        Object.keys(updateData).forEach(key => {
          currentToast[key] = updateData[key];
        });
        return [...currentToastsStack];
      }
      return currentToastsStack;
    });
  }, [setToastsStack]);
  const open = React.useCallback(toast => {
    const id = String(Date.now());
    setToastsStack(currentStack => {
      /**
       * Тосты могу появляться либо сверху вниз ("top-center", "top-right"), либо снизу вверх ("bottom-center", "bottom-right").
       * От этого зависит то, в каком порядке они должны добавляться в стек/удаляться из стека.
       */
      const appearsFromBottomToTop = placementFinal.includes('bottom');

      /**
       * Проверяем, переполнен ли стек тостов. Если да, то скрываем самый старый тост.
       */
      const isMaxToastsNumberExceeded = getIsMaxToastsNumberExceeded(currentStack, maxToastsNumberFinal);
      if (isMaxToastsNumberExceeded) {
        const oldestVisibleToastId = findOldestToastIdToHide(currentStack, appearsFromBottomToTop);
        if (oldestVisibleToastId) {
          hide(oldestVisibleToastId);
        }
      }

      /**
       * Проверяем, является ли тост скрываемым. Если да, то инициируем его скрытие по таймауту, сохраняем id таймера.
       */
      const isHidable = hidableToasts.includes(toast.type) && (toast.autoHide === undefined || toast.autoHide);
      let timerId;
      if (isHidable) {
        timerId = hideWithDelay(id, toast.hideDelay);
      }
      const nextToast = _objectSpread(_objectSpread({
        size
      }, toast), {}, {
        id,
        placement: placementFinal,
        timerId,
        isHidable
      });
      return appearsFromBottomToTop ? [...currentStack, nextToast] : [nextToast, ...currentStack];
    });
    return id;
  }, [setToastsStack, hide, hideWithDelay, maxToastsNumberFinal, placementFinal, size]);
  const api = React.useMemo(() => ({
    open,
    hide,
    hideWithDelay,
    hideAll,
    update
  }), [open, hide, hideWithDelay, hideAll, update]);
  React.useEffect(() => {
    toastsApiContext.setToastApiByPlacement(api, placementFinal);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, placementFinal]);
  React.useEffect(() => {
    if (indent) {
      toastsApiContext.setIndentByPlacement(placementFinal, indent);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placementFinal, indent]);
  return api;
}